import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { ReactSVG } from 'react-svg';
import { v4 as uuid } from 'uuid';
import { useIntl } from 'react-intl';
import messages from './messages';
import { Box, Button, CircularProgress, FormControl, FormControlLabel, Radio, RadioGroup, Switch, TextField, Typography } from '@mui/material';

import { getStyles } from './styles';
import { IconSvg } from '../../../../utils/globalIcons';
import { useAppContext } from '../../../../context/AppContext';
import { tostAlert } from '../../../../utils/AlertToast';
import LoadBar from '../../../../utils/LoadBar';
import { setAnalyticsDataSourcesState } from '../../../../Redux/slice/dataSlice';
import { setAnalyticsDataSourcesAdded } from '../../../../Redux/slice/newDataSlice';
import getAnalyticsDataSourceData from '../../../../utils/ApiFunctions/AnalyticsDataSourceData';
import AnalyticsDataSetsApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSets';
import { IS_COMMUNITY_MODE, IS_AWS_MODE } from '../../../../utils';

function SourceLists() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { selectedProject } = useAppContext();

    const [loading, setLoading] = useState(false);
    const [datasetName, setDatasetName] = useState('');
    const [connectors, setConnectors] = useState([])
    const [connectorsList, setConnectorsList] = useState([]);
    const [selectedConnectors, setSelectedConnectors] = useState([]);
    const [chatType, setChatType] = useState(null);

    const analytics_data_sources = useSelector(state => state.all_data.analytics_data_sources);
    const analytics_data_source_added = useSelector(state => state.data_added.analytics_data_source_added);
    const isExternalService = IS_COMMUNITY_MODE || IS_AWS_MODE;

    useEffect(() => {
        setChatType(null)
        getAllData();
    }, [])

    const custom_support = [
        { payload: { source_type: "links", __auto_id__: uuid() } },
        { payload: { source_type: "files", __auto_id__: uuid() } }
    ]

    const getAllData = async () => {
        setLoading(true)
        try {
            let all_sources = []
            if ((analytics_data_sources || [])?.length > 0 && analytics_data_source_added === false) {
                all_sources = analytics_data_sources
            } else {
                const response = await getAnalyticsDataSourceData(selectedProject?.payload?.__auto_id__);
                dispatch(setAnalyticsDataSourcesState(response))
                dispatch(setAnalyticsDataSourcesAdded(false))
                all_sources = response
            }
            const filtered_sources = (all_sources || [])?.filter(item => item?.payload?.status === "connected")
            const combined_connectors = [...filtered_sources, ...custom_support]
            setConnectors(combined_connectors)
            setConnectorsList(combined_connectors)
            return combined_connectors
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (event) => {
        const radio_value = event.target.value;
        setChatType(radio_value);
        setSelectedConnectors([]);

        if (radio_value === "analytics") {

            const updated_connectors = (connectorsList || []).filter(connector =>
                connector?.payload?.source_type !== "links" &&
                connector?.payload?.source_type !== "mongodb"
            );
            setConnectors(updated_connectors);

            setSelectedConnectors((prev) => {
                const hasLinks = prev.some(conn => conn?.payload?.source_type === "links");
                const hasMongo = prev.some(conn => conn?.payload?.source_type === "mongodb");

                if (hasMongo) {
                    tostAlert(intl.formatMessage(messages.mongo_excel_check), "warning");
                }
                if (hasLinks) {
                    tostAlert("Links are not allowed in analytics", "warning");
                }

                return prev.filter(conn =>
                    conn?.payload?.source_type !== "links" &&
                    conn?.payload?.source_type !== "mongodb"
                );
            });
        }


        if (radio_value === "chat_web") {

            const updated_connectors = (connectorsList || []).filter(connector =>
                connector?.payload?.source_type !== "ameya_extract" &&
                connector?.payload?.source_type !== "appflyte"
            );
            setConnectors(updated_connectors);

            setSelectedConnectors((prev) => {
                const hasExtract = prev.some(conn => conn?.payload?.source_type === "ameya_extract");
                const hasAppflyte = prev.some(conn => conn?.payload?.source_type === "appflyte");

                if (hasExtract || hasAppflyte) {
                    tostAlert(intl.formatMessage(messages.extraction_excel_check), "warning");
                }

                return prev.filter(conn =>
                    conn?.payload?.source_type !== "ameya_extract" &&
                    conn?.payload?.source_type !== "appflyte"
                );
            });
        }

    };

    const handleSwitchChange = (item) => {

        if (chatType === null) {
            tostAlert(intl.formatMessage(messages.usage_type_check), "warning");
            return;
        }

        setSelectedConnectors((prev) => {
            const isMongo = item?.payload?.source_type === "mongodb";
            const isExtract = item?.payload?.source_type === "ameya_extract";
            const isAppflyte = item?.payload?.source_type === "appflyte";

            const hasMongo = prev.some(conn => conn?.payload?.source_type === "mongodb");
            const hasExtract = prev.some(conn => conn?.payload?.source_type === "ameya_extract");
            const hasAppflyte = prev.some(conn => conn?.payload?.source_type === "appflyte");

            const existsSame = prev.some(conn => conn?.payload?.__auto_id__ === item?.payload?.__auto_id__);
            const hasConnectorType = prev.some(conn => conn?.payload?.source_type === item?.payload?.source_type);

            if (isMongo && chatType === "analytics") {
                tostAlert(intl.formatMessage(messages.mongo_excel_check), "warning");
                return prev;
            }

            if (isMongo) {
                if (hasMongo) {
                    return prev.filter(conn => conn?.payload?.source_type !== "mongodb");
                }
                if (prev.length > 0) {
                    tostAlert(intl.formatMessage(messages.mongo_check), "warning");
                    return prev;
                }
                return [item];
            }

            if (isExtract) {
                if (hasExtract) {
                    return prev.filter(conn => conn?.payload?.source_type !== "ameya_extract");
                }
                if (prev.length > 0) {
                    tostAlert(intl.formatMessage(messages.ameya_extract_check), "warning");
                    return prev;
                }
                return [item];
            }

            if (isAppflyte) {
                if (hasAppflyte) {
                    return prev.filter(conn => conn?.payload?.source_type !== "appflyte");
                }
                if (prev.length > 0) {
                    tostAlert(intl.formatMessage(messages.appflyte_check), "warning");
                    return prev;
                }
                return [item];
            }

            if (hasMongo) {
                tostAlert(intl.formatMessage(messages.mongo_check), "warning");
                return prev;
            }

            if (hasExtract) {
                tostAlert(intl.formatMessage(messages.ameya_extract_check), "warning");
                return prev;
            }

            if (hasAppflyte) {
                tostAlert(intl.formatMessage(messages.appflyte_check), "warning");
                return prev;
            }

            if (existsSame) {
                return prev.filter(conn => conn?.payload?.__auto_id__ !== item?.payload?.__auto_id__);
            }

            if (hasConnectorType) {
                return prev.filter(conn => conn?.payload?.source_type !== item?.payload?.source_type).concat(item);
            }
            return [...prev, item];
        });
    };

    const checkNameExistence = async () => {
        try {
            const project_id = selectedProject?.payload?.__auto_id__;
            const { data } = await AnalyticsDataSetsApi.checkNameExistence(project_id, datasetName);
            const collections = data?.published_collections_detail || [];
            const existingData = collections.flatMap(c => data[c.id] || []);
            return existingData.length > 0;
        } catch (err) {
            console.log(err);
            return false;
        }
    };

    const handleNext = async () => {
        setLoading(true)
        try {

            if (datasetName?.trim() === "") {
                tostAlert(intl.formatMessage(messages.dataset_name_check), "warning")
                return
            }

            if (selectedConnectors.length === 0) {
                tostAlert(intl.formatMessage(messages.source_select_check), "warning");
                return;
            }

            const nameExists = await checkNameExistence();
            if (nameExists) {
                tostAlert(intl.formatMessage(messages.dataset_name_existence), 'warning');
                return;
            }

            localStorage.setItem('dataset_id', JSON.stringify(uuid()));
            navigate('/data-sets/add', {
                state: {
                    source_types: selectedConnectors,
                    dataset_name: datasetName,
                    excel_analytics_status: chatType === "analytics"
                }
            });

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const connectorNames = {
        google_drive: "Google Drive",
        one_drive: "One Drive",
        aws_s3: "AWS S3",
        azure_blob: "Azure Blob",
        mongodb: "Mongo DB",
        mysql: "Mysql",
        ameya_extract: "Ameya Extract",
        links: "Links",
        files: "Files",
        gmail: "Gmail",
        outlook: "Outlook",
        appflyte: "Appflyte"
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                {loading && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>Add Dataset</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            onClick={(e) => handleNext()}
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                NEXT
                            </Typography>
                        </Button>
                        <Button
                            onClick={(e) => navigate('/data-sets')}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#404040' }}>
                                CANCEL
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ marginTop: '30px' }}>
                    <Typography mb={0.5}>Dataset Name <span style={{ color: 'red' }}>*</span></Typography>
                    <TextField
                        variant="outlined"
                        type="text"
                        size='small'
                        sx={{ width: '450px' }}
                        value={datasetName}
                        onChange={(e) => setDatasetName(e.target.value)}
                    />
                </Box>

                <Box marginTop={'20px'}>
                    <FormControl >
                        <Typography mb={0.5}>Usage <span style={{ color: 'red' }}>*</span></Typography>

                        <RadioGroup
                            aria-labelledby="bot-type-selection"
                            value={chatType}
                            onChange={handleChange}
                        >
                            <Box
                                sx={{
                                    bgcolor: "#FFFFFF",
                                    borderRadius: "5px",
                                    border: "1px solid #DEDEDE",
                                    cursor: "pointer",
                                    width: "450px",
                                    p: 1.5,
                                    mb: 1,
                                    "&:hover": { borderColor: "#59A292" },
                                }}
                            >
                                <FormControlLabel
                                    value="chat_web"
                                    control={
                                        <Radio
                                            sx={{
                                                color: "#59A292",
                                                "&.Mui-checked": { color: "#59A292" },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: "400",
                                                    fontSize: "14px",
                                                }}
                                            >
                                                Chat and Find
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontWeight: "400",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Find answer from any document or web links
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>

                            <Box
                                sx={{
                                    bgcolor: "#FFFFFF",
                                    borderRadius: "5px",
                                    border: "1px solid #DEDEDE",
                                    cursor: "pointer",
                                    width: "450px",
                                    p: 1.5,
                                    "&:hover": { borderColor: "#59A292" },
                                }}
                            >
                                <FormControlLabel
                                    value="analytics"
                                    control={
                                        <Radio
                                            sx={{
                                                color: "#59A292",
                                                "&.Mui-checked": { color: "#59A292" },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: "400",
                                                    fontSize: "14px",
                                                }}
                                            >
                                                Analytics
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontWeight: "400",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Excel documents, Appflyte, extracted documents by Ameya Extract
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Box marginTop={'20px'}>
                    <Typography mb={1}>Select Sources <span style={{ color: 'red' }}>*</span></Typography>

                    <Box mt={1}>
                        {loading ?
                            (<Box sx={{ textAlign: 'center', marginTop: '50px' }}>
                                <CircularProgress sx={{ color: '#0B51C5' }} />
                            </Box>)
                            :
                            (connectors || [])?.length > 0 ? (connectors?.map(item => (
                                <Box sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px',
                                    width: '450px', border: '1px solid #D9D9D9', borderRadius: '10px', marginBottom: '20px'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <ReactSVG
                                            src={{
                                                google_drive: IconSvg.driveIcon,
                                                one_drive: IconSvg.onedriveIcon,
                                                files: IconSvg.textFileIcon,
                                                aws_s3: IconSvg.awsIcon,
                                                links: IconSvg.wwwIcon,
                                                ameya_extract: IconSvg.ameyaIcon,
                                                mongodb: IconSvg.mongoIcon,
                                                gmail: IconSvg.mailIcon,
                                                outlook: IconSvg.mailIcon,
                                                appflyte: IconSvg.aiQmsIcon
                                            }[item?.payload?.source_type]}
                                            beforeInjection={(svg) => {
                                                svg.setAttribute('style', 'width:20px; height:20px; display:block; margin-right: 8px;');
                                                svg.setAttribute('stroke', '#0B51C5');
                                            }}
                                        />
                                        <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>
                                                {connectorNames[item?.payload?.source_type] || item?.payload?.source_type}
                                            </Typography>

                                            {(item?.payload?.source_type !== "links" || item?.payload?.source_type === "ameya_extract" || item?.payload?.source_type !== "files") &&
                                                <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
                                                    {item?.payload?.name}
                                                </Typography>
                                            }

                                        </Box>
                                        {(item?.payload?.source_type !== "mongodb" && item?.payload?.source_type !== "ameya_extract" && item?.payload?.source_type !== "files" && isExternalService) &&
                                            <Typography sx={{ fontSize: '14px', fontWeight: '500', marginLeft: '20px', color: "#9E9E9E" }}>
                                                Coming Soon
                                            </Typography>
                                        }
                                    </Box>
                                    <Box>
                                        <Switch
                                            disabled={item?.payload?.source_type !== "mongodb" && item?.payload?.source_type !== "ameya_extract" && item?.payload?.source_type !== "files" && isExternalService}
                                            checked={selectedConnectors.some(conn => conn?.payload?.__auto_id__ === item?.payload?.__auto_id__)}
                                            onChange={() => handleSwitchChange(item)}
                                            sx={{ width: '44', height: '28' }}
                                        />
                                    </Box>
                                </Box>
                            )))
                                :
                                (<Typography sx={{ textAlign: 'center', marginTop: '50px' }}>No Datasource Available</Typography>)
                        }
                    </Box>

                </Box>

            </Box>
        </Box >
    )
}

export default SourceLists;